import type { ReactNode } from 'react';
import { templatePlainText } from '@evogard/domain';

/**
 * A mensagem como ela vai chegar no WhatsApp — o balão, com as variáveis
 * preenchidas na ordem do texto (o OpaSuite preenche por POSIÇÃO; ver
 * `templateVariables`).
 *
 * É uma prévia, não o WhatsApp: a formatação aplicada é a do texto
 * (`*negrito*`, `_itálico_`, `~tachado~`), e o que o aparelho faz além disso —
 * pré-visualização de link, quebra de linha da tela — fica de fora. A posição
 * ainda vazia aparece como `{{n}}`, destacada, para quem preenche ver onde o
 * valor vai cair.
 */
export function WhatsappPreview({
  text,
  values,
  channel,
}: {
  text: string;
  values: string[];
  channel: string;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-borda-suave bg-superficie-suave">
      <p className="border-b border-borda-suave bg-superficie px-4 py-2.5 text-xs font-semibold text-texto-suave">
        Prévia · {channel}
      </p>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="ml-auto max-w-[92%] rounded-lg rounded-tr-none border border-borda-suave bg-sucesso-suave px-3 py-2 text-sm text-texto shadow-sm">
          <p className="whitespace-pre-wrap break-words">{render(text, values)}</p>
          <p className="mt-1 text-right text-xs text-texto-suave">agora</p>
        </div>
      </div>
    </div>
  );
}

/** Marcas que nenhum template tem: guardam a posição de cada variável no texto. */
const OPEN = '';
const CLOSE = '';

function render(text: string, values: string[]): ReactNode[] {
  let position = 0;
  const marked = templatePlainText(text).replace(/\{\{\s*[^{}]+?\s*\}\}/g, () => {
    const i = position++;
    const value = values[i]?.trim();
    // preenchida, o valor entra no texto e ganha a formatação de volta dele
    return value ? value : `${OPEN}${i}${CLOSE}`;
  });
  return format(marked, 'f');
}

/**
 * A formatação do WhatsApp, recursiva: `*negrito _itálico_*` vale. Um marcador
 * que não fecha na mesma linha não é marcador — o WhatsApp faz igual.
 */
function format(text: string, key: string): ReactNode[] {
  const match = /\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~/.exec(text);
  if (!match) return gaps(text, key);
  const before = text.slice(0, match.index);
  const after = text.slice(match.index + match[0].length);
  const inner = match[1] ?? match[2] ?? match[3] ?? '';
  const children = format(inner, `${key}i`);
  const node =
    match[1] !== undefined ? (
      <strong key={`${key}m`} className="font-semibold">
        {children}
      </strong>
    ) : match[2] !== undefined ? (
      <em key={`${key}m`}>{children}</em>
    ) : (
      <s key={`${key}m`}>{children}</s>
    );
  return [...gaps(before, `${key}b`), node, ...format(after, `${key}a`)];
}

/** As posições ainda vazias, como fichas `{{n}}`. */
function gaps(text: string, key: string): ReactNode[] {
  const parts = text.split(new RegExp(`${OPEN}(\\d+)${CLOSE}`));
  return parts.map((part, i) =>
    i % 2 === 0 ? (
      part
    ) : (
      <span
        key={`${key}${i}`}
        className="rounded bg-alerta-suave px-1 font-mono text-xs text-alerta"
      >
        {`{{${Number(part) + 1}}}`}
      </span>
    ),
  );
}
